/**
 * Kontrakt kolejności preflightu akcji ekonomii (Codex V-4 FIXES⁴ HIGH 1).
 *
 * Wydzielone z `runEconomyAction`, bo tam kolejność tonie w flush/pause/adopt
 * i nie da się jej przetestować bez przeglądarki, Zustand i Phasera. Tutaj jest
 * bez zależności, więc `tests/economyPreflight.test.mjs` pilnuje jej na stałe.
 *
 * Reguła: dopóki lokalny save jest oznaczony jako niepewny, **żadna** mutacja
 * ekonomii nie wychodzi na serwer. Najpierw resync; jeśli nie odzyskał
 * autorytatywnego stanu — akcja się NIE wykonuje.
 */

export class EconomyResyncFailedError extends Error {
  /** Błąd, który wywrócił resync (jeśli w ogóle rzucił). */
  public readonly cause?: unknown;
  constructor(cause?: unknown) {
    super('Nie udało się odświeżyć stanu gry — akcja wstrzymana.');
    this.name = 'EconomyResyncFailedError';
    if (cause !== undefined) this.cause = cause;
  }
}

export interface PreflightDeps<T> {
  /** Czy lokalny save jest oznaczony jako niepewny (globalna flaga). */
  isBlocked: () => boolean;
  /** Pełny resync; po sukcesie MUSI zdjąć flagę, inaczej uznajemy porażkę. */
  resync: () => Promise<void>;
  /** Właściwa mutacja — wolno ją wywołać wyłącznie po przejściu bramki. */
  action: () => Promise<T>;
}

/**
 * `isBlocked` → `resync` → `isBlocked` → `action`.
 *
 * Drugie sprawdzenie flagi jest istotne: resync może zakończyć się „sukcesem"
 * technicznym, a mimo to nie dać autorytatywnego save'a (np. `initialized:
 * false` dla konta, które przed chwilą wykonało operację skarbca). Wtedy stanu
 * NIE odzyskaliśmy i mutacja dalej jest zablokowana.
 */
export async function runWithPreflight<T>(deps: PreflightDeps<T>): Promise<T> {
  if (deps.isBlocked()) {
    try {
      await deps.resync();
    } catch (err) {
      throw new EconomyResyncFailedError(err);
    }
    if (deps.isBlocked()) throw new EconomyResyncFailedError();
  }
  return deps.action();
}

/**
 * Druga połowa kontraktu flagi (Codex V-4 FIXES⁶ HIGH 1): **każdy** resync —
 * nie tylko ten z preflightu — ustawia flagę w obie strony.
 *
 * ```text
 * autorytatywny save + adopcja   -> flaga false
 * dowolny błąd / brak save'a     -> flaga true, błąd propagowany
 * ```
 *
 * Dotąd podnosił ją tylko wołający, który o tym pamiętał, więc awaria resyncu
 * po `trade_completed` albo po niejednoznacznym merge'u zostawiała grę
 * odblokowaną — mimo że stan serwera JUŻ się zmienił. Jedna mutacja zdążyłaby
 * wyjść na niepewnym stanie, zanim cokolwiek by to wykryło.
 *
 * Blokada nie jest trwała: preflight przy następnej akcji spróbuje resyncu
 * jeszcze raz i zdejmie ją, gdy serwer wróci.
 */
export async function withResyncFlag<T>(deps: {
  setBlocked: (blocked: boolean) => void;
  resync: () => Promise<T>;
}): Promise<T> {
  let result: T;
  try {
    result = await deps.resync();
  } catch (err) {
    deps.setBlocked(true);
    throw err;
  }
  deps.setBlocked(false);
  return result;
}
